import BaseEntity     from '@/core/misc/BaseEntity';
import Core           from '@/core/Core';
import EntityPointer  from '@/domains/EntityPointer/EntityPointer';
import Title_ru_RU    from '@/core/misc/decorators/Title_ru_RU';
import TYPES          from '@/core/services/Changelogs/types';

class Comment extends BaseEntity {

	/**
	 * Окно редактирования коммента после создания.
	 * ВАЖНО: должно совпадать с CommentsService::ALLOWED_TIMESTAMP_ERROR_TEXT (25 минут на бэке).
	 * При изменении на бэке — менять и здесь.
	 */
	private static readonly EDIT_WINDOW_MS = 25 * 60 * 1000;

	static Events = {
		Created: 'comment:created',
		Updated: 'comment:updated',
		Deleted: 'comment:deleted',
		Read:    'comment:read',
	} as const;

	@Title_ru_RU('Текст комментария', TYPES.STRING)
	body: string | null = null;

	@Title_ru_RU('Последний комментарий', TYPES.BOOLEAN_MARK)
	isLast: boolean = false;

	@Title_ru_RU('Превью', TYPES.STRING)
	preview: string = '';

	parentEntity: EntityPointer | null = null;

  isRead: boolean = false;


  /**
	 * Создать новый, ещё не сохранённый коммент.
	 *
	 * Намеренно НЕ инициализирует id/createdAt/createdBy —
	 * эта сущность с id=null и есть «новая». Компонент должен корректно
	 * обрабатывать состояние «коммент существует локально, но ещё не на бэке».
	 */
	static fromNew(parentEntity: EntityPointer, body: string | null = null): Comment {
		const comment = new Comment();

		comment.parentEntity = parentEntity;
		comment.body = body;

		return comment;
	}

	static fromServer(data: any, comment = new Comment()): Comment {
		super.fromServer(data, comment);

		comment.body    = data.body ?? null;
		comment.preview = data.preview ?? '';
		comment.isLast  = data.isLast ?? false;

		if (data.parentEntity) {
			comment.parentEntity = new EntityPointer(
				data.parentEntity.typeName,
				data.parentEntity.id,
			);
		}

    comment.isRead = data.isRead;

		return comment;
	}

	/** Сохранён ли коммент на бэке */
	get isExisting(): boolean {
		return this.id !== null && this.id !== undefined;
	}

	/** Является ли коммент собственным */
	get isMine(): boolean {
		return this.createdBy?.id === Core.currentUser?.id;
	}

	/**
	 * Можно ли редактировать коммент по его собственным признакам:
	 * свой, сохранён на сервере, в окне 25 минут от создания.
	 *
	 * Не учитывает внешние состояния (например «коммент сейчас отправляется») —
	 * это ответственность вызывающего кода.
	 */
	get isEditable(): boolean {
		if (!this.isExisting) return false;
		if (!this.isMine)     return false;
		if (!this.createdAt)  return false;

		const createdAtMs = this.createdAt instanceof Date
			? this.createdAt.getTime()
			: new Date(this.createdAt as any).getTime();

		return Date.now() - createdAtMs < Comment.EDIT_WINDOW_MS;
	}
}

export default Comment;
